import { Component, inject, signal, effect } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RippleModule } from 'primeng/ripple';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogService } from 'primeng/dynamicdialog';
import { ERROR_HANDLER_PORT, NOTIFICATION_PORT } from '@app/ui/shared';
import { MonobankApiService } from '../monobank-api.service';
import { MonobankSettingsService } from '../monobank-settings.service';
import { MonobankAccountMappingDialogComponent } from './monobank-account-mapping-dialog.component';
import { ToggleSwitchModule } from 'primeng/toggleswitch';

@Component({
  selector: 'app-monobank-setting',
  standalone: true,
  imports: [
    RippleModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    FormsModule,
    TranslateModule,
    ToggleSwitchModule,
  ],
  template: `
    <div class="settings-item" pRipple (click)="onClick()" (keydown.enter)="onClick()" role="button" tabindex="0">
      <div>
        {{ 'screens.monobank.titles.integration' | translate }}
        <p>
          {{ 'screens.monobank.labels.integration_description' | translate }}
        </p>
      </div>
      <div>
        <p-toggleswitch [ngModel]="state.monobankEnabled()" [readonly]="true" />
        <!--
        <i class="pi" [class.pi-check-square]="state.monobankEnabled()" [class.pi-stop]="!state.monobankEnabled()"></i>
        -->
      </div>
    </div>

    <p-dialog
      [(visible)]="dialogVisible"
      [modal]="true"
      [closable]="true"
      [draggable]="false"
      [resizable]="false"
      styleClass="w-11/12 max-w-md"
      [header]="'screens.monobank.titles.setup' | translate"
    >
      <div class="flex flex-col gap-4">
        <div class="p-form-control">
          <input
            pInputText
            id="monobankToken"
            type="password"
            class="w-full"
            [(ngModel)]="token"
            [placeholder]="'screens.monobank.placeholders.enter_token' | translate"
          />
          <small class="text-muted-color">
            {{ 'screens.monobank.labels.token_help' | translate }}
            <a href="https://api.monobank.ua/" target="_blank" class="text-primary">{{
              'screens.monobank.labels.personal_cabinet' | translate
            }}</a>
          </small>
        </div>

        <div class="flex gap-2">
          <p-button
            [label]="'common.actions.cancel' | translate"
            severity="secondary"
            [outlined]="true"
            class="flex-1"
            styleClass="w-full"
            (onClick)="onCancel()"
          />
          <p-button
            [label]="'common.actions.ok' | translate"
            class="flex-1"
            styleClass="w-full"
            severity="primary"
            [outlined]="true"
            [disabled]="!token()"
            (onClick)="onSave()"
          />
        </div>
      </div>
    </p-dialog>
  `,
})
export class MonobankSettingComponent {
  protected readonly state = inject(MonobankSettingsService);
  private readonly monobankApi = inject(MonobankApiService);
  private readonly notifications = inject(NOTIFICATION_PORT);
  private readonly errorHandler = inject(ERROR_HANDLER_PORT);
  private readonly translate = inject(TranslateService);
  private readonly dialogService = inject(DialogService);

  readonly dialogVisible = signal(false);
  readonly token = signal('');

  constructor() {
    // Reset token when dialog closes
    effect(() => {
      if (!this.dialogVisible()) {
        this.token.set('');
      }
    });
  }

  onClick() {
    if (this.state.monobankEnabled()) {
      this.onConfigureMonobankAccounts();
    } else {
      this.dialogVisible.set(true);
    }
  }

  onConfigureMonobankAccounts(): void {
    this.dialogService.open(MonobankAccountMappingDialogComponent, {
      header: this.translate.instant('screens.monobank.titles.setup'),
      styleClass: 'p-dialog-form p-dialog-maximized',
      modal: true,
      closable: true,
    });
  }

  async onSave() {
    const tokenValue = this.token().trim();
    if (!tokenValue) {
      this.notifications.error(this.translate.instant('screens.monobank.placeholders.enter_token'));
      return;
    }

    try {
      await this.monobankApi.getClientInfo(tokenValue);
      await this.state.saveApiToken(tokenValue);

      if (!this.state.monobankEnabled()) {
        this.state.enableMonobank();
      }

      this.notifications.success(this.translate.instant('screens.monobank.notifications.api_token_saved'));
      this.dialogVisible.set(false);
      this.onConfigureMonobankAccounts();
    } catch (error) {
      this.errorHandler.handle(error, 'screens.monobank.notifications.token_validate_error');
    }
  }

  onCancel() {
    this.dialogVisible.set(false);
  }
}
